import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Factory,
  Plus,
  Edit2,
  Trash2,
  MapPin,
  Loader2,
  ChevronRight,
  ChevronDown,
  Users,
  QrCode,
  Download,
  Printer,
  Building2,
  Wrench,
  Layers,
  Grid3X3,
  Box,
  Cog,
  MoreHorizontal,
  GitBranch,
  List,
  AlertCircle,
  Archive,
  ArchiveRestore,
  Settings,
  Eye,
  EyeOff
} from 'lucide-react';
import { toast } from 'sonner';

import { API_BASE } from '../utils/apiClient';

const API = API_BASE;

const ZONE_TYPES = [
  { value: 'Production', label: 'Production' },
  { value: 'Entrepôt', label: 'Entrepôt' },
  { value: 'Maintenance', label: 'Maintenance' },
  { value: 'Qualité', label: 'Qualité' },
  { value: 'Sécurité', label: 'Sécurité' }
];

const UNIT_TYPES = [
  { value: 'Usine', label: 'Usine', icon: Factory },
  { value: 'Atelier', label: 'Atelier', icon: Wrench },
  { value: 'Ligne', label: 'Ligne', icon: Layers },
  { value: 'Secteur', label: 'Secteur', icon: Grid3X3 },
  { value: 'Zone', label: 'Zone', icon: MapPin },
  { value: 'Machine', label: 'Machine', icon: Cog },
  { value: 'Autre', label: 'Autre', icon: MoreHorizontal }
];

const Sites = () => {
  const { getAuthHeaders, isManager, isAdmin, isDemoReadonly } = useAuth();
  const { t } = useTranslation();
  const [sites, setSites] = useState([]);
  const [archivedSites, setArchivedSites] = useState([]);
  const [zones, setZones] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSite, setSelectedSite] = useState(null);

  // Dialogs
  const [siteDialogOpen, setSiteDialogOpen] = useState(false);
  const [zoneDialogOpen, setZoneDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archivedListOpen, setArchivedListOpen] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [editingZone, setEditingZone] = useState(null);
  const [siteToArchive, setSiteToArchive] = useState(null);
  const [selectedZoneForQR, setSelectedZoneForQR] = useState(null);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [loadingQR, setLoadingQR] = useState(false);

  // Forms
  const [siteForm, setSiteForm] = useState({ 
    name: '', 
    description: '', 
    unit_type: 'Usine', 
    unit_type_custom: '', 
    parent_id: '',
    responsible_id: ''
  });
  const [zoneForm, setZoneForm] = useState({ 
    name: '', 
    type: 'Production', 
    responsible_id: 'none',
    trs_objective: '',
    cadence_unit: 'unités/heure'
  });
  
  // View mode for sites list
  const [viewMode, setViewMode] = useState('hierarchy');
  const [expandedSites, setExpandedSites] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [sitesRes, zonesRes, usersRes] = await Promise.all([
        axios.get(`${API}/sites`, { headers: getAuthHeaders() }),
        axios.get(`${API}/zones`, { headers: getAuthHeaders() }),
        axios.get(`${API}/users`, { headers: getAuthHeaders() })
      ]);
      setSites(sitesRes.data);
      setZones(zonesRes.data);
      setUsers(usersRes.data);

      // Auto-select first root site if none selected
      const rootSites = sitesRes.data.filter(s => !s.parent_id);
      if (rootSites.length > 0 && !selectedSite) {
        setSelectedSite(rootSites[0]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const fetchArchivedSites = async () => {
    try {
      const response = await axios.get(`${API}/sites/archived`, { headers: getAuthHeaders() });
      setArchivedSites(response.data);
    } catch (error) {
      console.error('Error fetching archived sites:', error);
    }
  };

  // Build hierarchical tree structure
  const buildHierarchy = (sites) => {
    const sitesMap = {};
    sites.forEach(site => {
      sitesMap[site.id] = { ...site, children: [] };
    });
    
    const rootSites = [];
    sites.forEach(site => {
      if (site.parent_id && sitesMap[site.parent_id]) {
        sitesMap[site.parent_id].children.push(sitesMap[site.id]);
      } else {
        rootSites.push(sitesMap[site.id]);
      }
    });
    
    return rootSites;
  };

  // Get parent chain (breadcrumb)
  const getParentChain = (siteId) => {
    const chain = [];
    let current = sites.find(s => s.id === siteId);
    while (current?.parent_id) {
      const parent = sites.find(s => s.id === current.parent_id);
      if (parent) {
        chain.unshift(parent);
        current = parent;
      } else {
        break;
      }
    }
    return chain;
  };

  const handleSaveSite = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers.Authorization) {
        toast.error(t('auth.sessionExpired'));
        return;
      }
      
      const payload = {
        name: siteForm.name,
        description: siteForm.description || null,
        unit_type: siteForm.unit_type,
        unit_type_custom: siteForm.unit_type === 'Autre' ? siteForm.unit_type_custom : null,
        parent_id: siteForm.parent_id || null,
        responsible_id: siteForm.responsible_id || null
      };
      
      if (editingSite) {
        await axios.put(`${API}/sites/${editingSite.id}`, payload, { headers });
        toast.success(t('success.updated'));
        // Update selected site if it was the one being edited
        if (selectedSite?.id === editingSite.id) {
          setSelectedSite({ ...selectedSite, ...payload });
        }
      } else {
        await axios.post(`${API}/sites`, payload, { headers });
        toast.success(t('success.created'));
      }
      setSiteDialogOpen(false);
      setEditingSite(null);
      setSiteForm({ name: '', description: '', unit_type: 'Usine', unit_type_custom: '', parent_id: '', responsible_id: '' });
      fetchData();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error(t('auth.sessionExpired'));
        window.location.href = '/login';
      } else {
        toast.error(error.response?.data?.detail || t('errors.savingError'));
      }
    }
  };

  const handleArchiveSite = async (site, archive = true) => {
    try {
      await axios.patch(`${API}/sites/${site.id}/archive?archive=${archive}`, {}, { headers: getAuthHeaders() });
      toast.success(archive ? 'Unité archivée' : 'Unité restaurée');
      if (archive && selectedSite?.id === site.id) {
        setSelectedSite(null);
      }
      setArchiveDialogOpen(false);
      setSiteToArchive(null);
      fetchData();
      if (archivedListOpen) {
        fetchArchivedSites();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDeleteSite = async (site) => {
    if (!window.confirm(`${t('common.attention')}: ${t('common.confirmDelete')} "${site.name}" ?\n\n${t('common.irreversible')}. ${t('common.archiveInstead')}`)) return;
    try {
      await axios.delete(`${API}/sites/${site.id}`, { headers: getAuthHeaders() });
      toast.success('Unité supprimée définitivement');
      if (selectedSite?.id === site.id) setSelectedSite(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleSaveZone = async () => {
    try {
      const zoneData = {
        name: zoneForm.name,
        type: zoneForm.type,
        site_id: selectedSite.id,
        responsible_id: zoneForm.responsible_id === 'none' ? null : zoneForm.responsible_id,
        trs_objective: zoneForm.trs_objective ? parseFloat(zoneForm.trs_objective) : null,
        cadence_unit: zoneForm.cadence_unit || 'unités/heure'
      };

      if (editingZone) {
        await axios.put(`${API}/zones/${editingZone.id}`, zoneData, { headers: getAuthHeaders() });
        toast.success('Zone mise à jour');
      } else {
        await axios.post(`${API}/zones`, zoneData, { headers: getAuthHeaders() });
        toast.success('Zone créée');
      }
      setZoneDialogOpen(false);
      setEditingZone(null);
      setZoneForm({ name: '', type: 'Production', responsible_id: 'none', trs_objective: '', cadence_unit: 'unités/heure' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDeleteZone = async (zone) => {
    if (!window.confirm(`${t('sites.deleteZoneConfirm')} "${zone.name}" ?`)) return;
    try {
      await axios.delete(`${API}/zones/${zone.id}`, { headers: getAuthHeaders() });
      toast.success('Zone supprimée');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const openEditSite = (site) => {
    setEditingSite(site);
    setSiteForm({ 
      name: site.name, 
      description: site.description || '',
      unit_type: site.unit_type || 'Usine',
      unit_type_custom: site.unit_type_custom || '',
      parent_id: site.parent_id || '',
      responsible_id: site.responsible_id || ''
    });
    setSiteDialogOpen(true);
  };

  const openArchiveDialog = (site) => {
    setSiteToArchive(site);
    setArchiveDialogOpen(true);
  };

  const openArchivedList = () => {
    fetchArchivedSites();
    setArchivedListOpen(true);
  };

  const openEditZone = (zone) => {
    setEditingZone(zone);
    setZoneForm({ 
      name: zone.name, 
      type: zone.type, 
      responsible_id: zone.responsible_id || 'none',
      trs_objective: zone.trs_objective || '',
      cadence_unit: zone.cadence_unit || 'unités/heure'
    });
    setZoneDialogOpen(true);
  };

  const openNewSite = (parentId = null) => {
    setEditingSite(null);
    setSiteForm({ name: '', description: '', unit_type: 'Usine', unit_type_custom: '', parent_id: parentId || '', responsible_id: '' });
    setSiteDialogOpen(true);
  };

  const openNewZone = () => {
    setEditingZone(null);
    setZoneForm({ name: '', type: 'Production', responsible_id: 'none', trs_objective: '', cadence_unit: 'unités/heure' });
    setZoneDialogOpen(true);
  };

  const openQRCode = async (zone) => {
    setSelectedZoneForQR(zone);
    setQrDialogOpen(true);
    setLoadingQR(true);
    try {
      const response = await axios.get(`${API}/zones/${zone.id}/qrcode`, { headers: getAuthHeaders() });
      setQrCodeData(response.data);
    } catch (error) {
      toast.error('Erreur lors de la génération du QR code');
    } finally {
      setLoadingQR(false);
    }
  };

  const downloadQRPdf = async (zoneId) => {
    try {
      const response = await axios.get(`${API}/zones/${zoneId}/qrcode/pdf`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `qrcode_zone.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const printQR = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>QR Code - ${qrCodeData?.zone_name}</title>
          <style>
            body { font-family: Arial, sans-serif; text-align: center; padding: 40px; }
            h1 { color: #0E346E; margin-bottom: 10px; }
            h2 { color: #333; margin-bottom: 30px; }
            img { width: 300px; height: 300px; }
            p { color: #666; margin-top: 20px; }
          </style>
        </head>
        <body>
          <h1>GEMBACOACH</h1>
          <h2>${qrCodeData?.site_name} - ${qrCodeData?.zone_name}</h2>
          <img src="${qrCodeData?.qr_code}" alt="QR Code" />
          <p>Scannez ce QR code pour démarrer une tournée</p>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const siteZones = zones.filter(z => z.site_id === selectedSite?.id);
  const getUserName = (userId) => users.find(u => u.id === userId)?.name || 'Non assigné';

  const getUnitTypeInfo = (unitType, customType) => {
    const type = UNIT_TYPES.find(t => t.value === unitType) || UNIT_TYPES[0];
    const Icon = type.icon;
    const label = unitType === 'Autre' && customType ? customType : type.label;
    return { Icon, label };
  };

  const getUnitTypeBadge = (unitType, customType) => {
    const { label } = getUnitTypeInfo(unitType, customType);
    const colors = {
      'Usine': 'bg-blue-100 text-blue-800',
      'Atelier': 'bg-orange-100 text-orange-800',
      'Ligne': 'bg-purple-100 text-purple-800',
      'Secteur': 'bg-green-100 text-green-800',
      'Zone': 'bg-cyan-100 text-cyan-800',
      'Machine': 'bg-rose-100 text-rose-800',
      'Autre': 'bg-slate-100 text-slate-800'
    };
    return <Badge className={`${colors[unitType] || colors['Autre']} text-xs font-medium`}>{label}</Badge>;
  };

  const getZoneTypeBadge = (type) => {
    const colors = {
      Production: 'bg-blue-100 text-blue-800',
      Entrepôt: 'bg-purple-100 text-purple-800',
      Maintenance: 'bg-orange-100 text-orange-800',
      Qualité: 'bg-green-100 text-green-800',
      Sécurité: 'bg-red-100 text-red-800'
    };
    return <Badge className={colors[type] || 'bg-slate-100 text-slate-800'}>{type}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 space-y-6 fade-in" data-testid="sites-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Barlow_Condensed']">{t('sites.title')}</h1>
          <p className="text-slate-500 mt-1">{t('sites.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* View mode toggle */}
          <div className="flex border rounded-lg overflow-hidden">
            <Button
              variant={viewMode === 'hierarchy' ? 'default' : 'ghost'}
              size="sm"
              className={`rounded-none ${viewMode === 'hierarchy' ? 'bg-[#0E346E]' : ''}`}
              onClick={() => setViewMode('hierarchy')}
              title="Vue hiérarchique"
            >
              <GitBranch className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'flat' ? 'default' : 'ghost'}
              size="sm"
              className={`rounded-none ${viewMode === 'flat' ? 'bg-[#0E346E]' : ''}`}
              onClick={() => setViewMode('flat')}
              title="Vue liste"
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
          {isAdmin() && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={openArchivedList}
              className="text-slate-600"
              data-testid="view-archived-btn"
            >
              <Archive className="w-4 h-4 mr-1" />
              Archives
            </Button>
          )}
          {isManager() && !isDemoReadonly() && (
            <Button onClick={() => openNewSite()} className="bg-[#0E346E] hover:bg-[#0A2850]" data-testid="add-site-btn">
              <Plus className="w-4 h-4 mr-2" />
              {t('sites.newUnit')}
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sites List */}
        <div className="lg:col-span-1 space-y-3">
          <h2 className="text-lg font-semibold text-slate-700 flex items-center gap-2">
            <Factory className="w-5 h-5" />
            {t('sites.units')} ({sites.length})
          </h2>
          {sites.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-8 text-center">
                <Factory className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">{t('sites.noUnits')}</p>
                {isManager() && (
                  <Button variant="outline" size="sm" className="mt-3" onClick={() => openNewSite()}>
                    {t('sites.createUnit')}
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : viewMode === 'hierarchy' ? (
            // Hierarchical view - flat list with indentation
            <div className="space-y-1 bg-white rounded-lg border p-2">
              {(() => {
                // Flatten hierarchy with levels
                const flattenWithLevel = (nodes, level = 0) => {
                  let result = [];
                  nodes.forEach(node => {
                    result.push({ ...node, level });
                    if (node.children && node.children.length > 0 && expandedSites[node.id] !== false) {
                      result = result.concat(flattenWithLevel(node.children, level + 1));
                    }
                  });
                  return result;
                };
                const flatSites = flattenWithLevel(buildHierarchy(sites));
                
                return flatSites.map(site => {
                  const { Icon } = getUnitTypeInfo(site.unit_type, site.unit_type_custom);
                  const hasChildren = site.children && site.children.length > 0;
                  const zoneCount = zones.filter(z => z.site_id === site.id).length;
                  const isExpanded = expandedSites[site.id] !== false;
                  
                  return (
                    <div
                      key={site.id}
                      className={`flex items-center gap-2 p-3 rounded-lg cursor-pointer transition-all hover:bg-slate-50 group ${
                        selectedSite?.id === site.id ? 'bg-blue-50 ring-2 ring-blue-500' : ''
                      }`}
                      style={{ paddingLeft: `${site.level * 20 + 12}px` }}
                      onClick={() => setSelectedSite(site)}
                      data-testid={`site-card-${site.id}`}
                    >
                      {hasChildren ? (
                        <button 
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            setExpandedSites(prev => ({ ...prev, [site.id]: !isExpanded }));
                          }} 
                          className="p-1 hover:bg-slate-200 rounded"
                        >
                          {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                        </button>
                      ) : (
                        <div className="w-6" />
                      )}
                      
                      <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="w-4 h-4 text-slate-600" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-slate-900 truncate text-sm">{site.name}</h3>
                          {getUnitTypeBadge(site.unit_type, site.unit_type_custom)}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-slate-400">{zoneCount} {t('sites.zones')}</span>
                          {hasChildren && (
                            <span className="text-xs text-slate-400">• {site.children.length} {t('sites.subUnits')}</span>
                          )}
                        </div>
                      </div>
                      
                      {isManager() && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100"
                          onClick={(e) => { e.stopPropagation(); openNewSite(site.id); }}
                          title={t('sites.addSubUnit')}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          ) : (
            sites.map((site) => {
              const { Icon } = getUnitTypeInfo(site.unit_type, site.unit_type_custom);
              const parentChain = getParentChain(site.id);
              const zoneCount = zones.filter(z => z.site_id === site.id).length;
              return (
                <Card
                  key={site.id}
                  className={`cursor-pointer transition-all ${selectedSite?.id === site.id ? 'ring-2 ring-blue-500 border-blue-500' : 'hover:border-slate-300'}`}
                  onClick={() => setSelectedSite(site)}
                  data-testid={`site-card-${site.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Icon className="w-5 h-5 text-slate-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          {parentChain.length > 0 && (
                            <div className="flex items-center gap-1 text-xs text-slate-400 mb-1">
                              {parentChain.map((p, i) => (
                                <span key={p.id} className="flex items-center">
                                  {p.name}
                                  {i < parentChain.length - 1 && <ChevronRight className="w-3 h-3 mx-0.5" />}
                                </span>
                              ))}
                              <ChevronRight className="w-3 h-3 mx-0.5" />
                            </div>
                          )}
                          <h3 className="font-semibold text-slate-900 truncate">{site.name}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            {getUnitTypeBadge(site.unit_type, site.unit_type_custom)}
                            <span className="text-xs text-slate-400">{zoneCount} zone(s)</span>
                          </div>
                        </div>
                      </div>
                      <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform flex-shrink-0 ${selectedSite?.id === site.id ? 'rotate-90' : ''}`} />
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Zones for Selected Site */}
        <div className="lg:col-span-2">
          {selectedSite ? (
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div>
                    <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-blue-600" />
                      {selectedSite.name.toUpperCase()}
                    </CardTitle>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      {getUnitTypeBadge(selectedSite.unit_type, selectedSite.unit_type_custom)}
                      {selectedSite.responsible_id && (
                        <span className="text-sm text-slate-500 flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {getUserName(selectedSite.responsible_id)}
                        </span>
                      )}
                    </div>
                    {selectedSite.description && (
                      <p className="text-sm text-slate-500 mt-2">{selectedSite.description}</p>
                    )}
                  </div>
                  
                  {/* Actions Admin */}
                  {isManager() && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button 
                        variant="default" 
                        size="sm" 
                        onClick={() => openEditSite(selectedSite)}
                        className="bg-[#0E346E] hover:bg-[#0A2850]"
                        data-testid="edit-unit-btn"
                      >
                        <Edit2 className="w-4 h-4 mr-1" />
                        {t('sites.modifyUnit')}
                      </Button>
                      
                      {isAdmin() && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="text-amber-600 border-amber-300 hover:bg-amber-50"
                          onClick={() => openArchiveDialog(selectedSite)}
                          data-testid="archive-unit-btn"
                        >
                          <Archive className="w-4 h-4 mr-1" />
                          {t('sites.archive')}
                        </Button>
                      )}
                      
                      <Button 
                        size="sm" 
                        className="bg-green-600 hover:bg-green-700" 
                        onClick={openNewZone}
                        data-testid="add-zone-btn"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        {t('sites.addZone')}
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              
              <CardContent>
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  ZONES ({siteZones.length})
                </h3>
                
                {siteZones.length === 0 ? (
                  <div className="text-center py-8 border-2 border-dashed rounded-lg">
                    <MapPin className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">{t('sites.noZones')}</p>
                    {isManager() && (
                      <Button variant="outline" size="sm" className="mt-3" onClick={openNewZone}>
                        {t('sites.createZone')}
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {siteZones.map((zone) => (
                      <Card key={zone.id} className="border hover:shadow-md transition-shadow" data-testid={`zone-card-${zone.id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-slate-900">{zone.name}</h4>
                              <div className="flex items-center gap-2 mt-2">
                                {getZoneTypeBadge(zone.type)}
                              </div>
                              <p className="text-sm text-slate-500 mt-2 flex items-center gap-1">
                                <Users className="w-4 h-4" />
                                {getUserName(zone.responsible_id)}
                              </p>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => openQRCode(zone)}
                                title="Afficher QR Code"
                                data-testid={`qr-btn-${zone.id}`}
                              >
                                <QrCode className="w-4 h-4 text-blue-600" />
                              </Button>
                              {isManager() && (
                                <>
                                  <Button variant="ghost" size="sm" onClick={() => openEditZone(zone)}>
                                    <Edit2 className="w-4 h-4 text-slate-500" />
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => handleDeleteZone(zone)}>
                                    <Trash2 className="w-4 h-4 text-red-500" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="border-dashed">
              <CardContent className="py-16 text-center">
                <Factory className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">{t('sites.selectUnit')}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Site Dialog - Enhanced */}
      <Dialog open={siteDialogOpen} onOpenChange={setSiteDialogOpen}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader className="pb-4 border-b">
            <DialogTitle className="font-['Barlow_Condensed'] text-xl text-slate-900 flex items-center gap-2">
              {editingSite ? (
                <>
                  <Edit2 className="w-5 h-5 text-blue-600" />
                  {t('sites.editUnit')}
                </>
              ) : (
                <>
                  <Plus className="w-5 h-5 text-green-600" />
                  {t('sites.newUnit')}
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-5">
            {/* Nom de l'unité */}
            <div className="space-y-2">
              <Label htmlFor="site-name" className="text-sm font-semibold text-slate-700">
                {t('sites.unitName')} <span className="text-red-500">*</span>
              </Label>
              <Input
                id="site-name"
                value={siteForm.name}
                onChange={(e) => setSiteForm({ ...siteForm, name: e.target.value })}
                placeholder={t('sites.unitNamePlaceholder')}
                className="h-11 border-2 border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:ring-blue-500"
                data-testid="site-name-input"
              />
              {editingSite && (
                <p className="text-xs text-blue-600">
                  {t('sites.renameInfo')}
                </p>
              )}
            </div>

            {/* Type d'unité */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-slate-700">
                {t('sites.unitType')} <span className="text-red-500">*</span>
              </Label>
              <Select 
                value={siteForm.unit_type} 
                onValueChange={(v) => setSiteForm({ ...siteForm, unit_type: v })}
              >
                <SelectTrigger 
                  className="h-11 border-2 border-slate-200 bg-white text-slate-900 focus:border-blue-500 focus:ring-blue-500"
                  data-testid="unit-type-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {UNIT_TYPES.map((type) => {
                    const Icon = type.icon;
                    return (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4 text-slate-500" />
                          <span>{type.label}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Champ personnalisé si "Autre" */}
            {siteForm.unit_type === 'Autre' && (
              <div className="space-y-2">
                <Label htmlFor="custom-type" className="text-sm font-semibold text-slate-700">
                  {t('sites.customTypeName')} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="custom-type"
                  value={siteForm.unit_type_custom}
                  onChange={(e) => setSiteForm({ ...siteForm, unit_type_custom: e.target.value })}
                  placeholder={t('sites.customTypePlaceholder')}
                  className="h-11 border-2 border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:ring-blue-500"
                  data-testid="custom-type-input"
                />
              </div>
            )}

            {/* Unité parente (pour hiérarchie) */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-slate-700">
                {t('sites.parentUnit')} <span className="text-slate-400 font-normal">({t('common.optional')})</span>
              </Label>
              <Select 
                value={siteForm.parent_id || 'none'} 
                onValueChange={(v) => setSiteForm({ ...siteForm, parent_id: v === 'none' ? '' : v })}
              >
                <SelectTrigger 
                  className="h-11 border-2 border-slate-200 bg-white text-slate-900 focus:border-blue-500 focus:ring-blue-500"
                  data-testid="parent-unit-select"
                >
                  <SelectValue placeholder={t('common.select')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('sites.noParent')}</SelectItem>
                  {sites
                    .filter(s => s.id !== editingSite?.id)
                    .map((site) => {
                      const { Icon } = getUnitTypeInfo(site.unit_type, site.unit_type_custom);
                      const parentChain = getParentChain(site.id);
                      const fullPath = [...parentChain.map(p => p.name), site.name].join(' → ');
                      return (
                        <SelectItem key={site.id} value={site.id}>
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4 text-slate-500" />
                            <span className="truncate max-w-[200px]">{fullPath}</span>
                          </div>
                        </SelectItem>
                      );
                    })}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">{t('sites.parentHelp')}</p>
            </div>

            {/* Responsable */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-slate-700">
                {t('sites.responsible')} <span className="text-slate-400 font-normal">({t('common.optional')})</span>
              </Label>
              <Select 
                value={siteForm.responsible_id || 'none'} 
                onValueChange={(v) => setSiteForm({ ...siteForm, responsible_id: v === 'none' ? '' : v })}
              >
                <SelectTrigger 
                  className="h-11 border-2 border-slate-200 bg-white text-slate-900 focus:border-blue-500 focus:ring-blue-500"
                  data-testid="responsible-select"
                >
                  <SelectValue placeholder={t('common.select')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('sites.notAssigned')}</SelectItem>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-slate-500" />
                        <span>{user.name}</span>
                        <span className="text-xs text-slate-400">({user.role})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="site-desc" className="text-sm font-semibold text-slate-700">
                {t('common.description')} <span className="text-slate-400 font-normal">({t('common.optional')})</span>
              </Label>
              <Textarea
                id="site-desc"
                value={siteForm.description}
                onChange={(e) => setSiteForm({ ...siteForm, description: e.target.value })}
                placeholder={t('common.description')}
                rows={3}
                className="border-2 border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:ring-blue-500"
                data-testid="site-desc-input"
              />
            </div>
          </div>
          <DialogFooter className="pt-4 border-t">
            <Button variant="outline" onClick={() => setSiteDialogOpen(false)} className="border-2">
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleSaveSite} 
              disabled={!siteForm.name || (siteForm.unit_type === 'Autre' && !siteForm.unit_type_custom)} 
              className="bg-[#0E346E] hover:bg-[#0A2850]" 
              data-testid="save-site-btn"
            >
              {editingSite ? t('sites.saveChanges') : t('sites.createTheUnit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Confirmation Dialog */}
      <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <Archive className="w-5 h-5" />
              {t('sites.archiveUnit')}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p>
                  {t('sites.archiveConfirm').split('?')[0]} <strong>"{siteToArchive?.name}"</strong>.
                </p>
                <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
                  <p className="font-medium mb-1">{t('sites.archivePreserves')}</p>
                  <ul className="list-disc list-inside space-y-1 text-blue-700">
                    <li>{t('sites.archivePreservesList.history')}</li>
                    <li>{t('sites.archivePreservesList.kpis')}</li>
                    <li>{t('sites.archivePreservesList.links')}</li>
                  </ul>
                </div>
                <div className="bg-amber-50 p-3 rounded-lg text-sm text-amber-800">
                  <p className="font-medium mb-1">{t('sites.archiveEffect')}</p>
                  <p>{t('sites.archiveEffectDesc')}</p>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleArchiveSite(siteToArchive, true)}
              className="bg-amber-600 hover:bg-amber-700"
            >
              <Archive className="w-4 h-4 mr-1" />
              {t('sites.archive')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Archived Sites List Dialog */}
      <Dialog open={archivedListOpen} onOpenChange={setArchivedListOpen}>
        <DialogContent className="bg-white max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader className="pb-4 border-b">
            <DialogTitle className="font-['Barlow_Condensed'] text-xl text-slate-900 flex items-center gap-2">
              <Archive className="w-5 h-5 text-amber-600" />
              {t('sites.archivedUnits')}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {archivedSites.length === 0 ? (
              <div className="text-center py-8">
                <EyeOff className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">{t('sites.noArchivedUnits')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {archivedSites.map((site) => {
                  const { Icon } = getUnitTypeInfo(site.unit_type, site.unit_type_custom);
                  return (
                    <div 
                      key={site.id} 
                      className="flex items-center justify-between p-3 border rounded-lg bg-slate-50"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-slate-200 rounded-lg flex items-center justify-center">
                          <Icon className="w-5 h-5 text-slate-500" />
                        </div>
                        <div>
                          <h4 className="font-medium text-slate-700">{site.name}</h4>
                          <div className="flex items-center gap-2 mt-1">
                            {getUnitTypeBadge(site.unit_type, site.unit_type_custom)}
                            {site.archived_at && (
                              <span className="text-xs text-slate-400">
                                {t('sites.archivedOn')} {new Date(site.archived_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-green-600 border-green-300 hover:bg-green-50"
                        onClick={() => handleArchiveSite(site, false)}
                      >
                        <ArchiveRestore className="w-4 h-4 mr-1" />
                        {t('sites.restore')}
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <DialogFooter className="pt-4 border-t">
            <Button variant="outline" onClick={() => setArchivedListOpen(false)}>
              {t('common.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Zone Dialog */}
      <Dialog open={zoneDialogOpen} onOpenChange={setZoneDialogOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader className="pb-4 border-b">
            <DialogTitle className="font-['Barlow_Condensed'] text-xl text-slate-900">
              {editingZone ? t('sites.editZone') : t('sites.createZone').toUpperCase()}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-5">
            <div className="space-y-2">
              <Label htmlFor="zone-name" className="text-sm font-semibold text-slate-700">
                {t('sites.zoneName')} <span className="text-red-500">*</span>
              </Label>
              <Input
                id="zone-name"
                value={zoneForm.name}
                onChange={(e) => setZoneForm({ ...zoneForm, name: e.target.value })}
                placeholder={t('sites.zoneNamePlaceholder')}
                className="h-11 border-2 border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-slate-700">
                {t('sites.zoneType')} <span className="text-red-500">*</span>
              </Label>
              <Select value={zoneForm.type} onValueChange={(v) => setZoneForm({ ...zoneForm, type: v })}>
                <SelectTrigger className="h-11 border-2 border-slate-200 bg-white text-slate-900 focus:border-blue-500 focus:ring-blue-500">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ZONE_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-slate-700">
                {t('sites.responsible')} <span className="text-slate-400 font-normal">({t('common.optional')})</span>
              </Label>
              <Select value={zoneForm.responsible_id} onValueChange={(v) => setZoneForm({ ...zoneForm, responsible_id: v })}>
                <SelectTrigger className="h-11 border-2 border-slate-200 bg-white text-slate-900 focus:border-blue-500 focus:ring-blue-500">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('sites.notAssigned')}</SelectItem>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* TRS Configuration - Only for Production/Machine types - Admin only */}
            {(zoneForm.type === 'Production' || zoneForm.type === 'Machine') && (
              <div className="pt-4 border-t mt-4">
                <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Cog className="w-4 h-4 text-purple-600" />
                  Configuration TRS (Admin)
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-sm text-slate-600">
                      Objectif TRS (%)
                    </Label>
                    <Input
                      type="number"
                      step="1"
                      min="0"
                      max="100"
                      placeholder="ex: 85"
                      value={zoneForm.trs_objective}
                      onChange={(e) => setZoneForm({ ...zoneForm, trs_objective: e.target.value })}
                      className="h-10 border-2 border-slate-200 bg-white"
                    />
                    <p className="text-xs text-orange-600">
                      Une action sera créée si le TRS est inférieur à cet objectif
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-slate-600">
                      Unité de cadence
                    </Label>
                    <Select 
                      value={zoneForm.cadence_unit} 
                      onValueChange={(v) => setZoneForm({ ...zoneForm, cadence_unit: v })}
                    >
                      <SelectTrigger className="h-10 border-2 border-slate-200 bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unités/heure">unités/heure</SelectItem>
                        <SelectItem value="unités/minute">unités/minute</SelectItem>
                        <SelectItem value="pièces/heure">pièces/heure</SelectItem>
                        <SelectItem value="kg/heure">kg/heure</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Le superviseur saisira la cadence théorique lors de chaque tournée.
                </p>
              </div>
            )}
          </div>
          <DialogFooter className="pt-4 border-t">
            <Button variant="outline" onClick={() => setZoneDialogOpen(false)} className="border-2">
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSaveZone} disabled={!zoneForm.name} className="bg-[#0E346E] hover:bg-[#0A2850]">
              {editingZone ? t('common.save') : t('common.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-xl">
              {t('sites.qrCode')} - {selectedZoneForQR?.name}
            </DialogTitle>
          </DialogHeader>
          {loadingQR ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : qrCodeData && (
            <div className="text-center space-y-4">
              <div className="bg-white p-4 rounded-lg border inline-block">
                <img src={qrCodeData.qr_code} alt="QR Code" className="w-48 h-48 mx-auto" />
              </div>
              <p className="text-sm text-slate-600">
                {qrCodeData.site_name} • {qrCodeData.zone_name}
              </p>
              <div className="flex justify-center gap-2">
                <Button variant="outline" onClick={() => downloadQRPdf(selectedZoneForQR.id)}>
                  <Download className="w-4 h-4 mr-1" />
                  {t('sites.downloadPdf')}
                </Button>
                <Button onClick={printQR}>
                  <Printer className="w-4 h-4 mr-1" />
                  {t('sites.printQr')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Sites;
